import { z } from "zod";
/**
 * Primitive value schemas shared by every request contract.
 *
 * Everything monetary or dimensional is an INTEGER (spec §3, §11A):
 *   money    → paise          (₹67.50  → 6750)
 *   weight   → grams          (1.5 kg  → 1500)
 *   length   → millimetres    (2.4 m   → 2400)
 *   quantity → milli-units    (1.000   → 1000)
 *   GST rate → basis points   (18%     → 1800)
 * No schema in this package ever accepts a fractional money value.
 */
/**
 * The ceiling of a signed 32-bit column. Every money, quantity and stock column
 * in the schema is a Prisma `Int` (see prisma/schema.prisma) — 2,147,483,647
 * paise, about ₹2.15 crore.
 *
 * Bounding here rather than letting MariaDB decide is the same rule the
 * column-width guard enforces for strings: a Zod bound looser than its column is
 * a silent trap. The value passes validation, then the driver either raises an
 * opaque range error (a 500 where a clean 400 belonged) or — with strict mode
 * relaxed — clamps it, storing a rate the user never typed onto a document.
 */
export const INT32_MAX = 2_147_483_647;
/** Non-negative integer paise, bounded by the 32-bit money column. */
export const PaiseSchema = z
    .number()
    .int("Amount must be a whole number of paise — money is never fractional")
    .nonnegative("Amount cannot be negative")
    .max(INT32_MAX, "Amount is too large — the maximum is ₹2,14,74,836.47");
/** Quantity in milli-units: `1.000` is sent as `1000`. Must be > 0. */
export const QtyMilliSchema = z
    .number()
    .int("Quantity must be an integer number of milli-units (1.000 → 1000)")
    .positive("Quantity must be greater than zero");
/**
 * GST rate in basis points: 18% → 1800. Capped at 10000 (100%) as a sanity
 * guard; real GST slabs top out at 2800.
 */
export const GstRateBpSchema = z
    .number()
    .int("GST rate must be an integer number of basis points (18% → 1800)")
    .min(0, "GST rate cannot be negative")
    .max(10000, "GST rate cannot exceed 10000 basis points (100%)");
/** Piece count for BY_PIECE lines. */
export const PiecesSchema = z
    .number()
    .int("Pieces must be a whole number")
    .positive("Pieces must be greater than zero");
/** Weight in integer grams for BY_WEIGHT lines. */
export const WeightGramsSchema = z
    .number()
    .int("Weight must be an integer number of grams (1.5 kg → 1500)")
    .positive("Weight must be greater than zero");
/** Length in integer millimetres for BY_LENGTH lines. */
export const LengthMmSchema = z
    .number()
    .int("Length must be an integer number of millimetres (2.4 m → 2400)")
    .positive("Length must be greater than zero");
/**
 * Entity identifier. Deliberately permissive (`string | number`) because the
 * Prisma id strategy is not fixed in Phase 1 — an autoincrement `Int` and a
 * `String @default(cuid())` both satisfy this. Phase 2 should narrow it to
 * whichever the schema settles on.
 */
export const IdSchema = z.union([
    z.string().trim().min(1, "Id cannot be empty").max(64, "Id is too long"),
    z.number().int("Id must be an integer").positive("Id must be positive"),
]);
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})?)?$/;
/** Last day of the given 1-based month. Day 0 of the next month, computed in UTC. */
function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
/**
 * True only for a date that exists on the calendar.
 *
 * `Date.parse` cannot be used for this on its own: it silently rolls
 * `2026-02-30` over to 2 March rather than rejecting it, which would let an
 * invoice be dated a day that never happened.
 */
function isRealCalendarDate(value) {
    const match = ISO_DATE_PATTERN.exec(value);
    if (match === null)
        return false;
    const year = Number(match[1] ?? "0");
    const month = Number(match[2] ?? "0");
    const day = Number(match[3] ?? "0");
    if (month < 1 || month > 12)
        return false;
    if (day < 1 || day > daysInMonth(year, month))
        return false;
    // Catches out-of-range time components such as "T25:00".
    return !Number.isNaN(Date.parse(value));
}
/**
 * An ISO-8601 date (`2026-08-06`) or date-time. Rejects impossible calendar
 * dates such as `2026-02-30`.
 *
 * Kept as a string on the wire: the financial year and every displayed date are
 * resolved in Asia/Kolkata by `@steelbill/tax` (spec §5, §11A), never in the
 * machine-local zone, so a `Date` must not be constructed at the edge.
 */
export const IsoDateSchema = z
    .string()
    .trim()
    .regex(ISO_DATE_PATTERN, "Date must be ISO-8601, e.g. 2026-08-06")
    .refine(isRealCalendarDate, { message: "Date is not a real calendar date" });
/** Indian six-digit PIN code. */
export const PincodeSchema = z
    .string()
    .trim()
    .regex(/^[1-9][0-9]{5}$/, "PIN code must be six digits and cannot start with 0");
/**
 * Phone number, stored as typed. Loose on format — landlines and mobiles both
 * appear, with spaces, hyphens, parentheses and a country code all permitted.
 *
 * Capped at 15 to match the `@db.VarChar(15)` phone columns on company,
 * customers and users. That is tight enough to reject `+91 (020) 2588-1234`
 * (19 chars), which is why the message names the limit and suggests dropping
 * the separators rather than just saying "too long".
 */
export const PHONE_MAX = 15;
export const PhoneSchema = z
    .string()
    .trim()
    .min(6, "Phone number is too short")
    .max(PHONE_MAX, `Phone number must be ${PHONE_MAX} characters or fewer — try removing spaces or brackets`)
    .regex(/^[0-9+][0-9 ()+-]*$/, "Phone number contains unexpected characters");
/**
 * Maximum length for any string that MariaDB indexes. utf8mb4 uses up to four
 * bytes per character, so an indexed column is capped at `@db.VarChar(191)`
 * (spec §1) — validating at the edge keeps the DB from raising a truncation
 * error at insert time.
 */
export const INDEXED_STRING_MAX = 191;
/** A non-empty string that fits an indexed `VarChar(191)` column. */
export const IndexedStringSchema = z
    .string()
    .trim()
    .min(1, "Value is required")
    .max(INDEXED_STRING_MAX, `Must be ${INDEXED_STRING_MAX} characters or fewer`);
/** An optional free-text block (notes, terms, address lines). */
export function optionalText(max) {
    return z
        .string()
        .trim()
        .max(max, `Must be ${max} characters or fewer`)
        .optional();
}
/* -------------------------------------------------------------------------- */
/* Update (PUT) helpers                                                        */
/* -------------------------------------------------------------------------- */
/**
 * A field on an update request that can be LEFT ALONE or CLEARED.
 *
 * Absent key  → `undefined` → Prisma leaves the column untouched.
 * `null`      → `null`      → the column is set to NULL.
 * `""`        → `null`      → same thing, because an HTML input that the user
 *                             emptied posts an empty string, and storing `""`
 *                             in a nullable column would then print an empty
 *                             line on the invoice instead of omitting the field.
 *
 * The distinction matters: a PUT that omits `pan` must not wipe the PAN, and a
 * PUT that sends `pan: null` must. Collapsing the two — the usual outcome of
 * `.nullish()` plus a `?? existing` in the handler — makes one of those two
 * operations impossible to express.
 *
 * `z.preprocess` wraps the SCHEMA rather than replacing it, so a bad value still
 * fails with the schema's own message ("GSTIN format is invalid ..."), not with
 * the "Invalid input" that a `z.union` would produce.
 */
export function clearable(schema) {
    return z.preprocess((value) => (value === "" ? null : value), schema.nullable()).optional();
}
/* -------------------------------------------------------------------------- */
/* Query-string helpers                                                        */
/* -------------------------------------------------------------------------- */
/**
 * A query parameter is always a string, so `?active=false` arrives as the string
 * `"false"` — which is truthy. Anything that reads `req.query.active` as a
 * boolean without this is wrong in the one direction that matters (it would list
 * archived customers as active).
 *
 * `1` / `0` are accepted alongside `true` / `false` because both spellings show
 * up in hand-written URLs and in older HTTP clients.
 */
export const BooleanQuerySchema = z
    .enum(["true", "false", "1", "0"], {
    errorMap: () => ({ message: "Must be true or false" }),
})
    .transform((value) => value === "true" || value === "1");
/**
 * Treats an empty query parameter as absent. Spec §6 documents the endpoint as
 * `GET /customers?q=&active=`, i.e. the SPA sends every key whether or not the
 * user filled it in; `?limit=` must therefore mean "no limit given", not
 * `Number("") === 0`.
 */
export function optionalQuery(schema) {
    return z.preprocess((value) => (typeof value === "string" && value.trim() === "" ? undefined : value), schema);
}
/**
 * `:id` from the URL. Express hands over a string, so the coercion is the point:
 * a handler that passes `"12"` to a Prisma `Int` column gets a runtime error from
 * the driver instead of a 400 from the edge.
 *
 * Not `.strict()` — a router mounted under a parent path legitimately carries the
 * parent's params alongside its own.
 */
export const IdParamSchema = z.object({
    id: z.coerce
        .number({ invalid_type_error: "Id must be a number" })
        .int("Id must be a whole number")
        .positive("Id must be positive"),
});
//# sourceMappingURL=primitives.js.map